import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiText } from '@elastic/eui'

import { RootState } from 'app/rootReducer'
import { FormStack } from 'features/builders/agency/FormStack'
import { TvChannelDraft } from 'features/builders/agency/quickCampaignModels'

import { AcVideoUploader, UploadedVideo } from '../Basic/AcVideoUploader'
import { CreativeCard } from './CreativeCard'
import { CreativeLinkSettings, ICreativeLinkSettings } from './CreativeLinkSettings'

export interface IVideoAdUploader extends Omit<ICreativeLinkSettings, 'links' | 'onModeChange'> {
  draft: TvChannelDraft
  onVideosUploaded: (videos: UploadedVideo[]) => void
  onVideoRemove: (url: string) => void
  onIsUploadingChange: (isUploading: boolean) => void
}

const acceptedFileSize = 250 * 1000000

export const TvAdUploader: React.FC<IVideoAdUploader> = ({ idPrefix, draft, showErrors, onVideosUploaded, onVideoRemove, onIsUploadingChange, onSharedLinkChange }) => {
  const [isValidUpload, setIsValidUpload] = useState<boolean>(true)
  const { currentAccount } = useSelector((state: RootState) => state.app)

  return (
    <FormStack>
      <CreativeLinkSettings idPrefix={idPrefix} links={draft.links} showErrors={showErrors} onSharedLinkChange={onSharedLinkChange} />
      <EuiFormRow id={`${idPrefix}-uploader`} label='TV Ads' isInvalid={showErrors && draft.uploadedVideos.length === 0} error='Upload at least one TV ad'>
        <AcVideoUploader maxFileSize={acceptedFileSize} onUploadedUrlsChange={onVideosUploaded} onIsValidFileChange={setIsValidUpload} onIsUploadingChange={onIsUploadingChange} accountId={currentAccount!.id} allowMultiple />
      </EuiFormRow>
      {!isValidUpload && (
        <EuiText color='danger' size='s'>
          <p>Some videos could not be uploaded. Sizes must be less than {(acceptedFileSize / 1000000).toLocaleString()} MB</p>
        </EuiText>
      )}
      {draft.uploadedVideos.length > 0 && (
        <EuiFlexGroup wrap gutterSize='m' responsive={false}>
          {draft.uploadedVideos.map(video => (
            <EuiFlexItem grow={false} key={video.url}>
              <CreativeCard id={`${idPrefix}-${video.url.split('/').pop()}`} fileName={video.fileName} onRemove={() => onVideoRemove(video.url)}>
                <video controls preload='metadata' style={{ maxHeight: 150, maxWidth: '100%' }}>
                  <source src={video.url} type='video/mp4' />
                </video>
              </CreativeCard>
            </EuiFlexItem>
          ))}
        </EuiFlexGroup>
      )}
    </FormStack>
  )
}
